<template>
  <div class="screen">
    <h2>Telestrations</h2>
    <div v-if="roomError" class="notice notice--bad">{{ roomError }}</div>

    <template v-if="!roomId">
      <p class="muted">
        Everyone writes a phrase, draws the phrase they're handed, then guesses
        the drawing they're handed. Round and round, then you all look at what
        happened.
      </p>
      <!-- Your async games, across every game on the site (atrAsync, kept
           by the hub's Lambda): the ones waiting on you first. -->
      <div v-if="myGames.length" class="my-games">
        <h3 class="my-games__title">Your games</h3>
        <a v-for="g in myGames" :key="g.key" class="my-game" :class="{ 'is-my-turn': g.myTurn }" :href="g.url">
          <span class="my-game__name">{{ g.name }}<span v-if="g.code" class="my-game__code"> · {{ g.code }}</span></span>
          <span class="my-game__who">{{ g.myTurn ? 'Your turn' : (g.status === 'lobby' ? 'Not started' : g.waitingOn && g.waitingOn.length ? `Waiting on ${g.waitingOn.join(', ')}` : 'In progress') }}</span>
        </a>
      </div>

      <button class="primary" @click="host">Start a new game</button>
      <!-- Async play (2026-10-04): the room waits for people instead of
           handing their seats to bots, and the hub tells them when it's
           their turn. See src/store/asyncPlay.js. -->
      <label class="async-toggle">
        <input v-model="playAsync" type="checkbox" />
        <span>Play over days — the game waits for each person, and tells them when it's their turn</span>
      </label>
      <TurnBell :game="gamePrefix" />
      <div class="field">
        <label for="code">Or join with a code</label>
        <div class="join-row">
          <input id="code" v-model="code" class="code-input" maxlength="4"
                 autocomplete="off" spellcheck="false" placeholder="FROG" @keyup.enter="join" />
          <button class="secondary" :disabled="code.length < 4" @click="join">Join</button>
        </div>
      </div>
      <button class="secondary" @click="$emit('back')">Back</button>
    </template>

    <template v-else>
      <div class="room-code">
        <span class="room-code__label">Room code</span>
        <strong class="room-code__value">{{ roomMeta?.roomCode }}</strong>
        <button class="secondary" @click="share">{{ shareLabel }}</button>
      </div>

      <ul class="seats">
        <li v-for="s in seats" :key="s.uid" class="seat" :class="{ 'seat--bot': s.isBot }">
          <span class="seat__name">{{ s.displayName }}</span>
          <span v-if="s.isBot" class="seat__tag">robot</span>
          <span v-if="s.uid === roomMeta.hostUid" class="seat__tag">host</span>
          <span v-if="s.uid === myUid" class="seat__tag">you</span>
          <span v-if="!s.isBot" class="seat__dot" :class="{ 'seat__dot--on': presence[s.uid] }" />
        </li>
      </ul>

      <div v-if="isHost && botsAvailable" class="field">
        <label>Robots</label>
        <div class="segmented">
          <button
            v-for="n in botChoices"
            :key="n"
            :class="{ 'is-selected': n === botSeats.length }"
            @click="setBots(n)"
          >{{ n }}</button>
        </div>
        <span class="muted">
          They draw and guess for themselves, badly, which is the point.
        </span>
      </div>

      <p class="muted">
        {{ seats.length }} here.
        <template v-if="enoughPlayers">
          Every book runs {{ pages }} pages, so it goes round the table
          {{ lapText }}. Twelve turns each.
        </template>
        <template v-else>
          Needs at least {{ MIN_PLAYERS }}.
        </template>
      </p>

      <button v-if="isHost" class="primary" :disabled="!enoughPlayers" @click="deal">Start</button>
      <p v-else class="muted">Waiting for the host to start…</p>
      <button class="secondary" @click="leave">Leave room</button>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '@/firebase';
import { ASYNC_INDEX } from '@/store/asyncPlay.js';
import { GAMES } from '@/store/multiplayer';
import TurnBell from '@/components/shared/TurnBell.vue';
import { MIN_PLAYERS, MAX_PLAYERS, pageCount, lapsFor } from '@/engine/rounds';

export default {
  name: 'RoomLobby',
  components: { TurnBell },
  emits: ['back'],
  data() { return { playAsync: false, myGamesRows: {}, code: '', shareLabel: 'Copy link', MIN_PLAYERS }; },
  computed: {
    ...mapState(['roomId', 'roomMeta', 'presence', 'roomError']),
    gamePrefix() { return GAMES.replace(/Games$/, ''); },
    myGames() {
      return Object.entries(this.myGamesRows || {})
        .map(([key, row]) => ({ key, ...row }))
        .sort((a, b) => (b.myTurn - a.myTurn) || (b.updatedAt || 0) - (a.updatedAt || 0));
    },
    ...mapGetters(['isHost', 'myUid', 'seats', 'enoughPlayers', 'botSeats', 'humanSeats', 'botsAvailable']),
    botChoices() {
      const room = Math.max(0, MAX_PLAYERS - this.humanSeats.length);
      return Array.from({ length: Math.min(room, 5) + 1 }, (_, i) => i);
    },
    pages() { return pageCount(this.seats.length); },
    /**
     * Books lap on a small table, by design. Worth saying plainly in the
     * lobby: three people signing up for twelve turns each should know that
     * before they start, not discover it on page seven.
     */
    lapText() {
      const laps = lapsFor(this.seats.length);
      if (laps <= 1) return 'once';
      const whole = Number.isInteger(laps) ? laps : Math.round(laps * 10) / 10;
      return `${whole} times`;
    },
  },
  mounted() { this.$store.dispatch('sweepRooms'); this.followMyGames(); },
  beforeUnmount() { if (this.offMyGames) this.offMyGames(); },
  watch: {
    code(v) { this.code = String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); },
  },
  methods: {
    followMyGames() {
      const uid = this.$store.state.user?.uid;
      if (!uid || !db) return;
      this.offMyGames = onValue(dbRef(db, `${ASYNC_INDEX}/${uid}`), (snap) => { this.myGamesRows = snap.val() || {}; });
    },
    host() { this.$store.dispatch('hostRoom', { async: this.playAsync }); },
    join() { this.$store.dispatch('joinRoomByCode', this.code); },
    deal() { this.$store.dispatch('deal'); },
    setBots(n) { this.$store.dispatch('changeBotCount', n); },
    leave() { this.$store.dispatch('leaveRoom'); },
    async share() {
      // The invite carries the CODE as well as the link (Matt, 2026-10-04): on
      // an iPhone a link opens in Safari, never in the installed app, so a
      // friend who has the app wants the code to type into it instead.
      const code = this.roomMeta?.roomCode;
      const text = `Join me for Telestrations${this.roomMeta?.async ? ' — we play over days, so take your turns whenever' : ''}. Code ${code}`;
      const url = `${window.location.origin}/?join=${this.roomMeta?.roomCode}`;
      try {
        if (navigator.share) { await navigator.share({ title: 'Telestrations', text, url }); return; }
        await navigator.clipboard.writeText(`${text} · ${url}`);
        this.shareLabel = 'Copied';
        window.setTimeout(() => { this.shareLabel = 'Copy link'; }, 1800);
      } catch { this.shareLabel = 'Copy link'; }
    },
  },
};
</script>

<style scoped>
.my-games { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 0.5rem; }
.my-games__title { margin: 0 0 0.2rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.08em; opacity: 0.7; }
.my-game { display: flex; justify-content: space-between; gap: 0.75rem; padding: 0.6rem 0.8rem; border: 1px solid currentColor; border-radius: 0.6rem; text-decoration: none; color: inherit; opacity: 0.85; }
.my-game.is-my-turn { opacity: 1; font-weight: 700; }
.my-game__code { opacity: 0.7; font-weight: 400; }
.async-toggle { display: flex; gap: 0.6rem; align-items: flex-start; font-size: 0.9rem; line-height: 1.35; margin: 0.4rem 0; }
.async-toggle input { margin-top: 0.2rem; }
</style>
